/**
 * Cross-runtime quantity precision rules.
 *
 * Product variants currently store an arbitrary unit label, so precision is
 * intentionally conservative: only recognized measurement units allow
 * fractions. Unknown/custom units remain count-based until an explicit unit
 * configuration is added.
 */
export const FRACTIONAL_QUANTITY_PRECISION = 3;
// Physical measures can be fractional. Packaging, produce, and custom labels
// remain whole-number by default until an explicit conversion/configuration is
// added to the product model.
export const FRACTIONAL_UNIT_CODES = [
    'kg',
    'g',
    'tonne',
    'ton',
    'lb',
    'oz',
    'l',
    'litre',
    'liter',
    'ml',
    'gal',
    'm',
    'cm',
    'in',
    'ft',
    'yd',
];
const FRACTIONAL_UNIT_CODE_SET = new Set(FRACTIONAL_UNIT_CODES);
export function normalizeUnitOfMeasure(uom) {
    return uom.trim().toLowerCase();
}
export function quantityPrecisionForUnit(uom) {
    return FRACTIONAL_UNIT_CODE_SET.has(normalizeUnitOfMeasure(uom)) ? FRACTIONAL_QUANTITY_PRECISION : 0;
}
export function quantityInputStepForUnit(uom) {
    return quantityPrecisionForUnit(uom) === 0 ? '1' : '0.001';
}
/**
 * Checks decimal places without converting through JavaScript Number, which
 * avoids accepting or rejecting a value because of floating-point artifacts.
 * Sign and range checks remain the responsibility of the calling validator.
 */
export function hasQuantityPrecisionForUnit(value, uom) {
    const normalized = String(value).trim();
    const match = /^(?:\d+(?:\.(\d*))?|\.(\d*))$/.exec(normalized);
    if (!match)
        return false;
    const fractionalPart = match[1] ?? match[2];
    const precision = quantityPrecisionForUnit(uom);
    if (precision === 0) {
        return fractionalPart === undefined || /^0*$/.test(fractionalPart);
    }
    return (fractionalPart?.length ?? 0) <= precision;
}
export function formatQuantityForUnit(value, uom) {
    // Callers must validate the raw input first; this only canonicalizes a
    // finite numeric value for the persisted/display scale.
    return value.toFixed(quantityPrecisionForUnit(uom));
}
//# sourceMappingURL=quantity-precision.js.map